
import React, { useEffect, useState } from 'react';

import { AppBar, CssBaseline, Toolbar, Button, TextField, Paper } from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import MapIcon from '@mui/icons-material/Map';
import HistoryIcon from '@mui/icons-material/History';

import workDataImage from '../assets/images/workData.png';
import workMapImage from '../assets/images/workMap.png';
import workGraphImage from '../assets/images/workGraph.png';
import workErrorImage from '../assets/images/workError.png';
import worksListImage from '../assets/images/worksList.png';
import workRealTimeImage from '../assets/images/workRealTime.png';
import logo from '../assets/images/logo_dark.png';
import strings from '../values/strings/strings';
import colors from '../values/colors';
import styles from './HomePage.module.css';
import MakeContact from '../usecases/MakeContact';
import AuthCheckLogin from '../usecases/AuthCheckLogin';
import Pages from './Pages';

const cardPaperSx = {
    backgroundColor: colors.getTheme().palette.background.paper,
};

const benefitCards = [
    {
        title: strings.getString('screen_home_benefit_realtime_title'),
        description: strings.getString('screen_home_benefit_realtime_desc'),
        Icon: SpeedIcon,
    },
    {
        title: strings.getString('screen_home_benefit_maps_title'),
        description: strings.getString('screen_home_benefit_maps_desc'),
        Icon: MapIcon,
    },
    {
        title: strings.getString('screen_home_benefit_history_title'),
        description: strings.getString('screen_home_benefit_history_desc'),
        Icon: HistoryIcon,
    },
];

const dataOptions = [
    {
        title: strings.getString('screen_home_real_time_monitoring'),
        items: [
            strings.getString('screen_home_planted_area'),
            strings.getString('screen_home_seeds_per_meter'),
            strings.getString('screen_home_seeds_per_hectare'),
            strings.getString('screen_home_total_population'),
            strings.getString('screen_home_line_average_population'),
            strings.getString('screen_home_sprayed_area'),
            strings.getString('screen_home_current_pressure'),
            strings.getString('screen_home_target_flow_rate_lha'),
            strings.getString('screen_home_current_flow_rate_lha'),
            strings.getString('screen_home_current_flow_rate_lmin'),
            strings.getString('screen_home_liters_consumed'),
            strings.getString('screen_home_current_temperature'),
        ],
    },
        {
        title: strings.getString('screen_home_planting_and_spraying_work_log'),
        items: [
            strings.getString('screen_home_planted_area'),
            strings.getString('screen_home_seeds_per_meter'),
            strings.getString('screen_home_seeds_per_hectare'),
            strings.getString('screen_home_total_population'),
            strings.getString('screen_home_line_average_population'),
            strings.getString('screen_home_sprayed_area'),
            strings.getString('screen_home_general_flow_rate_lha'),
            strings.getString('screen_home_general_flow_rate_lmin'),
            strings.getString('screen_home_liters_consumed'),
            strings.getString('screen_home_average_temperature'),
        ],
    },
        {
        title: strings.getString('screen_home_reports_maps_graphs'),
        items: [
            strings.getString('screen_home_seeds_per_meter'),
            strings.getString('screen_home_seeds_per_hectare'),
            strings.getString('screen_home_hectares_per_hour'),
            strings.getString('screen_home_liters_per_minute'),
            strings.getString('screen_home_pressure'),
            strings.getString('screen_home_temperature'),
            strings.getString('screen_home_velocity'),
            strings.getString('screen_home_relief'),
        ],
    },
]

const DataList = ({ data }) => {
    return (
        <div className={styles.dataList}>
            <h3 className={styles.title}>{data.title}</h3>
            <ul>
                {data.items.map((item, index) => (
                    <li key={data.title + item}>{item}{index === data.items.length - 1 ? '.' : ';'}</li>
                ))}
            </ul>
        </div>
    );
}

const BenefitCard = ({ title, description, Icon }) => {
    return (
        <Paper className={styles.benefitCard} sx={cardPaperSx} elevation={1}>
            <Icon className={styles.benefitIcon} aria-hidden />
            <h3 className={styles.benefitTitle}>{title}</h3>
            <p className={styles.benefitDescription}>{description}</p>
        </Paper>
    );
}

const Testimony = ({ children, author }) => {
    return (
        <div className={styles.testimony}>
            <p>{children}</p>
            <p className={styles.author}>- {author}</p>
        </div>
    );
}

function HomePage(props) {
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    useEffect(() => {
        const checkLogin = async () => {
            const userData = await AuthCheckLogin().catch(() => null);
            setIsLoggedIn(Boolean(userData));
        };
        checkLogin();
    }, []);

    const handleSend = async () => {
        try {
            setLoading(true);

            await MakeContact(email, phone);
        } catch (e) {
            console.error('Error submitting contact info:', e);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <CssBaseline />
            <AppBar>
                <Toolbar className={styles.toolbar}>
                    <img src={logo} alt={strings.getString("screen_home_logo")} className={styles.logo} />
                    {isLoggedIn ? (
                        <Button href={Pages.routes.Dashboard}>{strings.getString("screen_login_signin")}</Button>
                    ) : (
                        <div className={styles.headerActions}>
                            <Button href={Pages.routes.Login}>{strings.getString("info_login")}</Button>
                            <Button href={Pages.routes.SignUp}>{strings.getString("screen_login_singup")}</Button>
                        </div>
                    )}
                </Toolbar>
            </AppBar>
            <div className={styles.content}>
                <h1 className={styles.title}>{strings.getString("screen_home_title")}</h1>
                <h2 className={styles.headline}>{strings.getString("screen_home_headline")}</h2>
                <p>{strings.getString("screen_home_description_1")}</p>
                <p>{strings.getString("screen_home_description_2")}</p>
                <div className={styles.benefitCardsContainer}>
                    {benefitCards.map((card) => (
                        <BenefitCard
                            key={card.title}
                            title={card.title}
                            description={card.description}
                            Icon={card.Icon}
                        />
                    ))}
                </div>
                <img src={workRealTimeImage} alt={strings.getString("screen_home_work_realtime_image")} className={styles.workImage} />
                <img src={workDataImage} alt={strings.getString("screen_home_work_data_image")} className={styles.workImage} />
                <img src={workMapImage} alt={strings.getString("screen_home_work_map_image")} className={styles.workImage} />
                <img src={workGraphImage} alt={strings.getString("screen_home_work_graph_image")} className={styles.workImage} />
                <img src={workErrorImage} alt={strings.getString("screen_home_work_error_image")} className={styles.workImage} />
                <img src={worksListImage} alt={strings.getString("screen_home_works_list_image")} className={styles.workImage} />
                <h2 className={styles.title}>{strings.getString("screen_home_subtitle")}</h2>
                <Paper className={styles.dataOptionsContainer} sx={cardPaperSx} elevation={1}>
                    <DataList
                        data={dataOptions[0]}
                    />
                    <DataList
                        data={dataOptions[1]}
                    />
                    <DataList
                        data={dataOptions[2]}
                    />
                </Paper>
                {/* <h2 className={styles.title}>{strings.getString("screen_home_testimonials")}</h2>
                <div className={styles.testimonialsContainer}>
                    <Testimony author="Seu Fulano">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vivamus vel leo et arcu vulputate hendrerit. Quisque enim dui, laoreet vitae ex sit amet, consequat viverra elit. Nullam ut risus faucibus, consectetur augue ut, auctor lorem. Ut efficitur erat quis erat auctor, eget euismod lorem bibendum. Cras egestas nibh ut erat scelerisque, nec aliquet libero egestas. Vestibulum dignissim, nunc sed congue fringilla, orci nisl maximus risus, ut mattis lorem sapien quis felis. Nunc molestie metus non dui posuere tristique. Donec fermentum dui et pellentesque accumsan. Suspendisse rhoncus, elit et sagittis vulputate, orci lacus gravida ligula, in viverra est tortor in nunc. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Fusce id arcu et leo efficitur varius. Aliquam ligula odio, dictum quis euismod in, viverra at quam. Curabitur vehicula in lorem vel sagittis.</Testimony>
                    <Testimony author="Seu Beltrano">Sed dignissim augue in malesuada varius. Donec at accumsan neque, non sollicitudin mi. Mauris sit amet mauris diam. Nulla et ligula ac nulla suscipit euismod. Integer augue est, ultrices lacinia erat ut, tempor auctor risus. Aliquam erat volutpat. Integer sed sem ut nisl lobortis rutrum. Vivamus ultrices justo eu lacus volutpat condimentum vitae et magna. Nam quis risus iaculis nibh luctus consectetur.</Testimony>
                    <Testimony author="Seu Sicrano">Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. In facilisis ipsum lacus, nec ultrices odio vehicula malesuada. Donec cursus blandit felis, non sagittis ante tempus et. In hac habitasse platea dictumst. Quisque consectetur tellus porttitor diam molestie volutpat. Aenean fringilla tellus enim, ac pharetra elit faucibus vel. In ultrices nibh et venenatis accumsan. Mauris efficitur finibus mauris in rhoncus. Phasellus fermentum facilisis tellus, in sollicitudin ligula iaculis eget. Nunc porta vulputate est nec maximus. Aenean non risus convallis, efficitur est vitae, luctus est. Aenean aliquet massa odio, sed varius massa scelerisque ac. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce pretium et nisi eu malesuada. Duis vitae urna euismod, condimentum ipsum non, volutpat mi.</Testimony>
                </div>
                <h2 className={styles.title}>{strings.getString("screen_home_contact")}</h2>
                <div className={styles.contactContainer}>
                    <div className={styles.contactDescription}>{strings.getString("screen_home_contact_message")}</div>
                    <div className={styles.contactForm}>
                        <div className={styles.contactInputs}>
                            <TextField
                                variant='standard'
                                label={strings.getString("screen_home_email")}
                                onChange={e => setEmail(e.target.value)}
                                className={styles.contactInput}
                            />
                            <TextField
                                variant='standard'
                                label={strings.getString("screen_home_phone")}
                                onChange={e => setPhone(e.target.value)}
                                className={styles.contactInput}
                            />
                        </div>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSend}
                            disabled={loading}
                        >
                            {strings.getString("info_send")}
                        </Button>
                    </div>
                </div> */}

            </div>
        </div>
    );
}

export default HomePage;